/**
 * Carte "Entrée dans le dispositif" — affichée dans le profil bénéficiaire
 * (admin, référent, animateur).
 *
 * Fonctionnalités :
 *   - Statut actuel : pas d'invitation | en attente | accepté | refusé
 *   - Action principale : "Envoyer l'invitation par email" (consentement à distance)
 *   - Action secondaire : "Valider sur place" (consentement signé en présentiel)
 *   - Date d'envoi, date de réponse et historique des invitations
 *
 * Toutes les actions visibles aux rôles : admin, animateur, referent.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from './ui/dialog';
import {
  ClipboardCheck, Send, RefreshCw, CheckCircle2,
  XCircle, Clock, AlertCircle, ChevronDown, ChevronUp,
  PenLine
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

// ── Helpers ──────────────────────────────────────────────────────────────────
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
});

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

// ── Badge statut ──────────────────────────────────────────────────────────────
const StatutBadge = ({ statut }) => {
  const configs = {
    accepte:    { label: 'Accepté',    color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
    en_attente: { label: 'En attente', color: 'bg-amber-100 text-amber-800 border-amber-200',       icon: <Clock         className="w-3.5 h-3.5" /> },
    refuse:     { label: 'Refusé',     color: 'bg-red-100 text-red-800 border-red-200',             icon: <XCircle       className="w-3.5 h-3.5" /> },
    revoque:    { label: 'Révoqué',    color: 'bg-slate-100 text-slate-600 border-slate-200',       icon: <RefreshCw     className="w-3.5 h-3.5" /> },
    non_envoye: { label: 'Non envoyé', color: 'bg-slate-100 text-slate-500 border-slate-200',       icon: <AlertCircle   className="w-3.5 h-3.5" /> },
  };
  const c = configs[statut] || configs.non_envoye;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${c.color}`}>
      {c.icon}{c.label}
    </span>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────
export default function EntreeDispositifCard({
  bénéficiaireId,
  bénéficiaireEmail,
  bénéficiaireNom,
  onValidated,
}) {
  const [validations, setValidations] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [sending, setSending]         = useState(false);
  const [validating, setValidating]   = useState(false);
  const [message, setMessage]         = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [feedback, setFeedback]       = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // Dialog "Valider sur place"
  const [showValidDialog, setShowValidDialog] = useState(false);
  const [validComment, setValidComment]       = useState('');
  const sigRef = useRef(null);

  // ── Charger l'historique des invitations ─────────────────────────────────
  const load = useCallback(async () => {
    if (!bénéficiaireId) return;
    try {
      const r = await fetch(`${API_BASE}/entree-dispositif/${bénéficiaireId}`, {
        headers: authHeaders(),
      });
      if (r.ok) {
        const data = await r.json();
        setValidations(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('[EntreeDispositifCard] load error:', e);
    } finally {
      setLoading(false);
    }
  }, [bénéficiaireId]);

  useEffect(() => { load(); }, [load]);

  const derniere     = validations.find(v => v.statut !== 'revoque') || validations[0];
  const statutActuel = derniere?.statut || 'non_envoye';
  const dejaAccepte  = statutActuel === 'accepte';

  // ── Envoyer l'invitation par email ────────────────────────────────────────
  const handleEnvoyer = async () => {
    if (!bénéficiaireEmail) {
      setFeedback({ type: 'err', text: "Ce bénéficiaire n'a pas d'adresse email renseignée. Utilisez « Valider sur place »." });
      return;
    }
    setSending(true);
    setFeedback(null);
    try {
      const r = await fetch(`${API_BASE}/entree-dispositif/envoyer`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          apprenant_id: bénéficiaireId,
          message_personnalise: message.trim() || null,
        }),
      });
      const data = await r.json();
      if (r.ok) {
        setFeedback({ type: 'ok', text: 'Invitation envoyée par email !' });
        setMessage('');
        setShowMessage(false);
        await load();
      } else {
        setFeedback({ type: 'err', text: data.detail || "Erreur lors de l'envoi." });
      }
    } catch {
      setFeedback({ type: 'err', text: 'Erreur réseau. Vérifiez votre connexion.' });
    } finally {
      setSending(false);
    }
  };

  // ── Valider manuellement (consentement signé en présentiel) ──────────────
  const handleValiderSurPlace = async () => {
    setValidating(true);
    setFeedback(null);
    try {
      let signature = null;
      if (sigRef.current && !sigRef.current.isEmpty()) {
        // react-signature-canvas v1.1.0-alpha n'expose plus getTrimmedCanvas() :
        // on utilise toDataURL() directement sur l'instance.
        signature = sigRef.current.toDataURL('image/png');
        if (!signature || signature.length < 100) {
          setFeedback({ type: 'err', text: "La signature n'a pas pu être capturée. Veuillez la refaire." });
          setValidating(false);
          return;
        }
      }
      const r = await fetch(
        `${API_BASE}/entree-dispositif/valider-admin/${bénéficiaireId}`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            signature,
            commentaire: validComment.trim() || null,
          }),
        }
      );
      const data = await r.json();
      if (r.ok) {
        setShowValidDialog(false);
        setValidComment('');
        sigRef.current?.clear();
        setFeedback({
          type: 'ok',
          text: `${bénéficiaireNom || 'Le bénéficiaire'} est désormais actif dans le dispositif.`
        });
        await load();
        if (onValidated) onValidated();
      } else {
        setFeedback({ type: 'err', text: data.detail || 'Erreur lors de la validation.' });
      }
    } catch {
      setFeedback({ type: 'err', text: 'Erreur réseau. Vérifiez votre connexion.' });
    } finally {
      setValidating(false);
    }
  };

  // ── Libellé du bouton email ──────────────────────────────────────────────
  const emailBtnLabel = () => {
    if (dejaAccepte) return null;
    if (statutActuel === 'en_attente') return "Renvoyer l'invitation par email";
    return "Envoyer l'invitation par email";
  };

  if (loading) {
    return (
      <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
        <CardContent className="py-6 text-center text-slate-400 text-sm">
          Chargement…
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle
            className="text-lg font-bold flex items-center gap-2"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            <ClipboardCheck className="w-5 h-5 text-blue-600" />
            Entrée dans le dispositif
          </CardTitle>
          <StatutBadge statut={statutActuel} />
        </CardHeader>

        <CardContent className="space-y-4">

          {/* ── Infos dernière invitation ─────────────────────────────── */}
          {derniere && (
            <div className="text-sm text-slate-600 space-y-1 bg-slate-50 rounded-xl p-3">
              <div className="flex justify-between">
                <span className="text-slate-500">
                  {derniere.validation_admin ? 'Validé le' : 'Invitation envoyée le'}
                </span>
                <span className="font-medium">{fmt(derniere.date_envoi)}</span>
              </div>
              {derniere.date_reponse && !derniere.validation_admin && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Réponse le</span>
                  <span className="font-medium">{fmt(derniere.date_reponse)}</span>
                </div>
              )}
              {derniere.envoye_par_nom && (
                <div className="flex justify-between">
                  <span className="text-slate-500">
                    {derniere.validation_admin ? 'Validé par' : 'Envoyée par'}
                  </span>
                  <span className="font-medium">{derniere.envoye_par_nom}</span>
                </div>
              )}
              {derniere.commentaire_refus && (
                <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-100 text-red-700 text-xs">
                  <span className="font-semibold">Motif de refus : </span>
                  {derniere.commentaire_refus}
                </div>
              )}
            </div>
          )}

          {/* ── Accepté ───────────────────────────────────────────────── */}
          {dejaAccepte && (
            <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-100 text-emerald-800 text-sm font-medium">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              Bénéficiaire actif dans le dispositif.
            </div>
          )}

          {/* ── Pas encore envoyé ────────────────────────────────────── */}
          {statutActuel === 'non_envoye' && (
            <p className="text-sm text-slate-500">
              Aucun consentement enregistré. Envoyez l'invitation par email
              <em> ou </em> validez sur place si le bénéficiaire a signé en présentiel.
            </p>
          )}

          {/* ── En attente ───────────────────────────────────────────── */}
          {statutActuel === 'en_attente' && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl border border-amber-100 text-amber-800 text-sm">
              <Clock className="w-4 h-4 shrink-0" />
              En attente de réponse. Vous pouvez renvoyer le mail ou valider sur place.
            </div>
          )}

          {/* ── Refusé ───────────────────────────────────────────────── */}
          {statutActuel === 'refuse' && (
            <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl border border-red-100 text-red-800 text-sm">
              <XCircle className="w-4 h-4 shrink-0" />
              Le bénéficiaire a refusé. Vous pouvez renvoyer une invitation si sa situation a changé.
            </div>
          )}

          {/* ── Feedback ─────────────────────────────────────────────── */}
          {feedback && (
            <div className={`flex items-center gap-2 p-3 rounded-xl text-sm font-medium
              ${feedback.type === 'ok'
                ? 'bg-emerald-50 border border-emerald-100 text-emerald-800'
                : 'bg-red-50 border border-red-100 text-red-800'}`}>
              {feedback.type === 'ok'
                ? <CheckCircle2 className="w-4 h-4 shrink-0" />
                : <AlertCircle  className="w-4 h-4 shrink-0" />}
              {feedback.text}
            </div>
          )}

          {/* ── Actions (cachées si déjà accepté) ────────────────────── */}
          {!dejaAccepte && (
            <div className="space-y-3 pt-1">
              {/* Message personnalisé pliable */}
              <button
                type="button"
                onClick={() => setShowMessage(v => !v)}
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                {showMessage ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {showMessage ? 'Masquer le message' : 'Ajouter un message personnalisé (email)'}
              </button>

              {showMessage && (
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={3}
                  placeholder="Ex : Bonjour, suite à notre entretien, je vous invite à confirmer votre participation."
                  className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
              )}

              {/* Boutons d'action côte à côte */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button
                  onClick={handleEnvoyer}
                  disabled={sending || !bénéficiaireEmail}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                  data-testid="envoyer-invitation-btn"
                  title={!bénéficiaireEmail ? "Aucun email renseigné" : ""}
                >
                  {sending
                    ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Envoi…</>
                    : <><Send className="w-4 h-4 mr-2" />{emailBtnLabel()}</>}
                </Button>

                <Button
                  onClick={() => { setFeedback(null); setShowValidDialog(true); }}
                  variant="outline"
                  className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-xl"
                  data-testid="valider-sur-place-btn"
                >
                  <PenLine className="w-4 h-4 mr-2" />
                  Valider sur place
                </Button>
              </div>

              {!bénéficiaireEmail && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2">
                  Pas d'email renseigné — utilisez « Valider sur place » après signature manuscrite.
                </p>
              )}
            </div>
          )}

          {/* ── Historique ─────────────────────────────────────────────── */}
          {validations.length > 1 && (
            <div>
              <button
                type="button"
                onClick={() => setShowHistory(v => !v)}
                className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
              >
                {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {showHistory ? 'Masquer' : 'Voir'} l'historique ({validations.length} entrée{validations.length > 1 ? 's' : ''})
              </button>

              {showHistory && (
                <div className="mt-2 space-y-2">
                  {validations.map((v, i) => (
                    <div key={v.id || i} className="flex items-center justify-between text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                      <span>{fmt(v.date_envoi)}</span>
                      <StatutBadge statut={v.statut} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </CardContent>
      </Card>

      {/* ── Dialog "Valider sur place" ─────────────────────────────────── */}
      <Dialog open={showValidDialog} onOpenChange={setShowValidDialog}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5 text-emerald-600" />
              Valider l'entrée sur place
            </DialogTitle>
            <DialogDescription>
              Le bénéficiaire signe directement ici (présentiel). Il sera immédiatement
              marqué <strong>actif</strong> dans le dispositif.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Signature du bénéficiaire <span className="text-slate-400">(optionnelle)</span>
              </label>
              <div className="border-2 border-emerald-200 rounded-xl overflow-hidden bg-white">
                <SignatureCanvas
                  ref={sigRef}
                  penColor="#1e3a5f"
                  canvasProps={{ className: 'w-full', style: { height: 140, width: '100%' } }}
                />
              </div>
              <button
                type="button"
                onClick={() => sigRef.current?.clear()}
                className="mt-1 text-xs text-slate-400 hover:text-slate-600"
              >
                Effacer la signature
              </button>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Commentaire <span className="text-slate-400">(optionnel)</span>
              </label>
              <textarea
                value={validComment}
                onChange={e => setValidComment(e.target.value)}
                rows={2}
                placeholder="Ex : Consentement signé sur papier le 23/05/2026 — copie scannée jointe au dossier."
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-300"
              />
            </div>

            <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
              En validant, vous attestez avoir recueilli le consentement éclairé du
              bénéficiaire pour son entrée dans le dispositif (RGPD).
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowValidDialog(false)}
              disabled={validating}
              className="rounded-xl"
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleValiderSurPlace}
              disabled={validating}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
              data-testid="confirm-valider-sur-place"
            >
              {validating
                ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Validation…</>
                : <><CheckCircle2 className="w-4 h-4 mr-2" />Valider l'entrée</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
